import type { ReactNode } from 'react'
import { forwardRef } from 'react'

import type { BoxPropsAs } from '@jotta/ui/Box'

import { ErrorMessage } from '../../Elements/ErrorMessage/ErrorMessage'
import { DeprecatedDialog } from '../Dialog/DeprecatedDialog'
import { DialogFooter } from '../Dialog/Dialog'

export interface RadixDialogProps {
  /** Main component title */
  title?: string
  /** Small title placed above main title */
  smallTitle?: string
  /** Additional content below buttons */
  footer?: ReactNode
  /** Callback to run when close button or background is clicked */
  onClose?: (open: boolean) => void
  skipCloseButton?: boolean
  errorMessage?: string | Error | null
  /** Array of buttons */
  buttons?: {
    /** Button text */
    label: string
    /** Button click handler */
    action: 'close' | React.MouseEventHandler | ((...props: any[]) => void)
    type: 'destructive' | 'primary' | 'outline'
    visible?: boolean
    disabled?: boolean
    loading?: boolean
    testId?: string
    ref?: (ref: HTMLButtonElement | null) => void
  }[]
  customButtons?: ReactNode
  /** Passed on to the dialog content */
  'data-testid'?: string
}

/**
 * @deprecated Use <Dialog/> instead
 */
export const RadixDialog = forwardRef<
  HTMLDivElement,
  BoxPropsAs<'div', RadixDialogProps, 'all'>
>(function RadixDialog(
  {
    title,
    smallTitle,
    errorMessage,
    buttons,
    footer,
    children,
    onClose,
    skipCloseButton,
    customButtons,
    'data-testid': testId,
  },
  ref,
) {
  const closeHandler = () => {
    if (onClose) {
      onClose(false)
    } else {
      console.warn('No close handler registered for dialog')
    }
  }
  return (
    <DeprecatedDialog
      secondaryTitle={smallTitle}
      title={title}
      testId={testId}
      buttons={buttons}
      onClose={!skipCloseButton ? closeHandler : undefined}
    >
      <ErrorMessage className="m-4" error={errorMessage} />
      {children}
      {customButtons ? customButtons : null}
      {footer && <DialogFooter>{footer}</DialogFooter>}
    </DeprecatedDialog>
  )
})
